import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useParams, useNavigate } from "react-router-dom";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import API_URL from "../../api/API";

export const AdminCreateStaticSpeedWorkout = () => {
  const { staff } = useStaffAuthContext();
  const { template_id } = useParams(); // Use this if you need an ID from the URL for editing
  const navigate = useNavigate();

  const [templateName, setTemplateName] = useState('');
  const [week, setWeek] = useState('');
  const [days, setDays] = useState([{ name: '', themes: [{ title: '', sprints: [] }] }]);
  const [existingWorkouts, setExistingWorkouts] = useState([]);

  // Modal state for confirmation
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  // State to trigger refetch after a workout is saved
  const [isWorkoutSaved, setIsWorkoutSaved] = useState(false);

  // Fetch template name separately
  const fetchTemplateName = async () => {
    try {
      const response = await fetch(API_URL.GET_STATIC_WORKOUT_TEMPLATE + template_id, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplateName(data.title); // Set template name
      } else {
        console.error("Failed to fetch template name");
      }
    } catch (error) {
      console.error("Error fetching template name:", error);
    }
  };

  // Fetch template data (workouts)
  const fetchTemplateData = async () => {
    try {
      const response = await fetch(API_URL.GET_STATIC_WORKOUT + template_id, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log(data);
        setExistingWorkouts(data.workouts); // Assuming the API returns an array of workouts
      } else {
        console.error("Failed to fetch template data");
      }
    } catch (error) {
      console.error("Error fetching template data:", error);
    }
  };

  // Fetch template name and workouts when the component mounts or when the token changes
  useEffect(() => {
    fetchTemplateName();
    fetchTemplateData();
  }, [staff.token]); // Run only when the staff token changes

  // Trigger refetch after saving a workout
  useEffect(() => {
    if (isWorkoutSaved) {
      fetchTemplateData(); // Refetch template data
      setIsWorkoutSaved(false); // Reset after fetching
    }
  }, [isWorkoutSaved]); // Refetch after saving

  // Add a new sprint to a theme
  const handleAddSprint = (dayIndex, themeIndex) => {
    const updatedDays = [...days];
    updatedDays[dayIndex].themes[themeIndex].sprints.push({
      sets: 0,
      distance: 0,
      reps: 0,
      rest: '',
      notes: '',
      video_url: '',
    });
    setDays(updatedDays);
  };

  // Delete a sprint from a theme
  const handleDeleteSprint = (dayIndex, themeIndex, sprintIndex) => {
    const updatedDays = [...days];
    updatedDays[dayIndex].themes[themeIndex].sprints = updatedDays[dayIndex].themes[themeIndex].sprints.filter((_, index) => index !== sprintIndex);
    setDays(updatedDays);
  };

  // Handle input change for day, theme, or sprint fields
  const handleInputChange = (e, field, dayIndex, themeIndex, sprintIndex) => {
    const updatedDays = [...days];
    if (sprintIndex !== undefined) {
      updatedDays[dayIndex].themes[themeIndex].sprints[sprintIndex][field] = e.target.value;
    } else if (themeIndex !== undefined) {
      updatedDays[dayIndex].themes[themeIndex][field] = e.target.value;
    } else {
      updatedDays[dayIndex][field] = e.target.value;
    }
    setDays(updatedDays);
  };

  const handleSave = async () => {
    // Validate the data before saving
    if (!week || !days.some(day => day.name && day.themes.length > 0)) {
      alert("Please fill out the required fields.");
      return;
    }

    const data = { week, days, template_id };

    try {
      const response = await fetch(API_URL.POST_SAVE_STATIC_WORKOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        alert('Static workout saved successfully!');
        setWeek('');
        setDays([{ name: '', themes: [{ title: '', sprints: [] }] }]); // Reset form
        setIsWorkoutSaved(true); // Mark that the workout was saved
      } else {
        const error = await response.json();
        alert(`Failed to save workout: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving workout:', error);
      alert('Failed to save workout.');
    }
  };

  // Handle Edit (redirect to editing page)
  const handleEditTheme = (workout_id, theme_id) => {
    navigate(`/administrator/static-speed-workout/edit/${workout_id}/${theme_id}`);
  };

  // Handle Delete (open modal)
  const handleDeleteWorkout = (workout_id, theme_id) => {
    setSelectedWorkout(workout_id);
    setSelectedTheme(theme_id);
    setIsModalOpen(true);
  };

  // Perform deletion action
  const confirmDelete = async () => {
    try {
      const response = await fetch(API_URL.DELETE_STATIC_WORKOUT_BY_THEME, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${staff.token}`,
        },
        body: JSON.stringify({
          workout_id: selectedWorkout,
          theme_id: selectedTheme,
        }),
      });

      if (response.ok) {
        alert('Workout deleted successfully!');
        setIsModalOpen(false);
        fetchTemplateData(); // Reload the list
      } else {
        const error = await response.json();
        alert(`Failed to delete workout: ${error.error}`);
      }
    } catch (error) {
      console.error("Error deleting workout:", error);
      alert('Failed to delete workout.');
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-grow p-6 flex flex-row">
          {/* Left Section: Manage Existing Static Workout */}
          <div className="w-1/2 p-6 border-r-2 h-[80vh] overflow-y-auto bg-gray-50 shadow-sm scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
            <h2 className="text-2xl font-bold mb-4 text-gray-700">Manage Existing Themes</h2>
            <h3 className="text-xl font-semibold mb-4 text-gray-600">Template: {templateName}</h3>
            {existingWorkouts.length > 0 ? (
              existingWorkouts.map((workout, workoutIndex) => (
                <div key={workoutIndex} className="mb-6">
                  <h3 className="text-lg font-semibold text-indigo-600">{workout.week}</h3>
                  {workout.days.map((day, dayIndex) => (
                    <div key={dayIndex} className="ml-6 mt-2">
                      <h4 className="text-md font-semibold text-blue-500">{day.name}</h4>
                      {day.themes.map((theme, themeIndex) => {
                        const totalDistance = theme.sprints.reduce((sum, sprint) => sum + sprint.distance, 0);
                        return (
                          <div key={themeIndex} className="ml-4 p-4 bg-white rounded-lg shadow-md mb-3">
                            <div className="flex justify-between items-center">
                              <span className="text-md font-semibold text-gray-800">{theme.title}</span>
                              <span className="text-sm font-semibold text-green-600">
                                Total Distance: {totalDistance} meters
                              </span>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => handleEditTheme(workout._id, theme._id)}
                                className="text-blue-500 hover:underline mr-4"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteWorkout(workout._id, theme._id)}
                                className="text-red-500 hover:underline"
                              >
                                Delete
                              </button>
                            </div>
                            <div className="ml-4 mt-2">
                              {theme.sprints.map((sprint, sprintIndex) => (
                                <div key={sprintIndex} className="text-sm text-gray-700">
                                  <p>Sets: {sprint.sets}, Distance: {sprint.distance} meters</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p className="text-gray-500 italic bg-gray-100 p-4 rounded-lg shadow-sm">
                No existing workouts found.
              </p>
            )}
          </div>


         {/* Right Section: New Workout Creation Form */}
        <div className="w-1/2 p-4">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Go Back
          </button>
          <h1 className="text-2xl font-bold mb-4">Build Static Speed Workout</h1>
          <h2 className="text-xl font-semibold mb-4">Template: {templateName}</h2>
          
          <div className="mb-4">
            <label className="block text-lg font-semibold mb-1">Week</label>
            <select
              className="border p-2 w-full"
              value={week}
              onChange={(e) => setWeek(e.target.value)}
            >
              <option value="">Select Week</option>
              {[...Array(8).keys()].map((i) => (
                <option key={i} value={`Week ${i + 1}`}>
                  Week {i + 1}
                </option>
              ))}
            </select>
          </div>

          {days.map((day, dayIndex) => (
            <div key={dayIndex} className="mb-6">
              <div className="mb-4">
                <label className="block text-lg font-semibold mb-1">Day Name</label>
                <select
                  className="border p-2 w-full"
                  value={day.name}
                  onChange={(e) => handleInputChange(e, 'name', dayIndex)}
                >
                  <option value="">Select Day</option>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                    (dayName) => (
                      <option key={dayName} value={dayName}>
                        {dayName}
                      </option>
                    )
                  )}
                </select>
              </div>

              {day.themes.map((theme, themeIndex) => (
                <div key={themeIndex} className="mb-4">
                  <div className="mb-4">
                    <label className="block text-lg font-semibold mb-1">Theme Title</label>
                    <input
                      type="text"
                      className="border p-2 w-full"
                      placeholder="Theme Title"
                      value={theme.title}
                      onChange={(e) => handleInputChange(e, 'title', dayIndex, themeIndex)}
                    />
                  </div>

                  {theme.sprints.map((sprint, sprintIndex) => (
                      <div key={sprintIndex} className="mb-4">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-lg font-semibold mb-1">Sets</label>
                            <input
                              type="number"
                              className="border p-2 w-full"
                              placeholder="Sets"
                              value={sprint.sets}
                              onChange={(e) => handleInputChange(e, 'sets', dayIndex, themeIndex, sprintIndex)}
                            />
                          </div>
                          <div>
                            <label className="block text-lg font-semibold mb-1">Reps</label>
                            <input
                              type="number"
                              className="border p-2 w-full"
                              placeholder="Reps"
                              value={sprint.reps}
                              onChange={(e) => handleInputChange(e, 'reps', dayIndex, themeIndex, sprintIndex)}
                            />
                          </div>
                          <div>
                            <label className="block text-lg font-semibold mb-1">Distance</label>
                            <input
                              type="number"
                              className="border p-2 w-full"
                              placeholder="Distance"
                              value={sprint.distance}
                              onChange={(e) => handleInputChange(e, 'distance', dayIndex, themeIndex, sprintIndex)}
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-lg font-semibold mb-1">Rest</label>
                          <textarea
                            className="border p-2 w-full"
                            placeholder="Rest"
                            value={sprint.rest}
                            onChange={(e) => handleInputChange(e, 'rest', dayIndex, themeIndex, sprintIndex)}
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-lg font-semibold mb-1">Notes</label>
                          <textarea
                            className="border p-2 w-full"
                            placeholder="Notes"
                            value={sprint.notes}
                            onChange={(e) => handleInputChange(e, 'notes', dayIndex, themeIndex, sprintIndex)}
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-lg font-semibold mb-1">Video URL</label>
                          <input
                            type="text"
                            className="border p-2 w-full"
                            placeholder="Video URL"
                            value={sprint.video_url}
                            onChange={(e) => handleInputChange(e, 'video_url', dayIndex, themeIndex, sprintIndex)}
                          />
                        </div>

                        <div className="flex justify-end">
                          <button
                            onClick={() => handleDeleteSprint(dayIndex, themeIndex, sprintIndex)}
                            className="text-red-500 hover:underline"
                          >
                            Delete Sprint
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleAddSprint(dayIndex, themeIndex)}
                        className="bg-blue-500 text-white p-2 rounded mt-2"
                      >
                        Add Sprint
                      </button>
                    </div>
                </div>
              ))}
            </div>
          ))}
              <div className="flex justify-center">
                <button
                  onClick={handleSave}
                  className="bg-green-500 text-white p-2 rounded mt-4"
                >
                  Save Workout
                </button>
              </div>
        
        </div>
      </div>

      {/* Modal Popup for Delete Confirmation */}
      {isModalOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Are you sure you want to delete this workout?</h3>
            <button
              onClick={confirmDelete}
              className="bg-red-500 text-white p-2 rounded mr-4"
            >
              Yes
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              className="bg-gray-500 text-white p-2 rounded"
            >
              No
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
